import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Alert,
  Box,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useAnalytics, useAuth } from "@/providers";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { DatasetRecord } from "@/shared/domain/datasets";
import { useCreateDataset } from "@/shared/domain/datasets/hooks";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import { RobotoDomainException } from "@/shared/http";

import { MetadataForm, MetadataView } from "../Metadata";
import { RobotoTooltip } from "../RobotoTooltip";
import { AutocompleteType } from "../TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "../tags";

interface CreateDatasetFormProps {
  onSuccess?: (dataset: DatasetRecord) => void;
}

export function CreateDatasetForm({ onSuccess }: CreateDatasetFormProps) {
  const theme = useTheme();

  const [tags, setTags] = React.useState<string[]>([]);
  const [name, setName] = React.useState<string>("");
  const [metadata, setMetadata] = React.useState<Record<string, unknown>>({});
  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});
  const { currentOrganization } = useAuth();
  const { trackEvent } = useAnalytics();

  const createDatasetMutation = useCreateDataset();

  const createDataset = () => {
    if (!currentOrganization?.org_id) {
      return;
    }

    const isNameEmpty = name.trim() === "";

    createDatasetMutation.mutate(
      {
        request: {
          tags: tags,
          metadata: metadata,
          name: isNameEmpty ? undefined : name,
          custom_fields:
            Object.keys(customFieldValues).length > 0
              ? customFieldValues
              : undefined,
        },
        resourceOwnerId: currentOrganization.org_id,
      },
      {
        onSuccess,
      },
    );
  };

  return (
    <Box>
      <Box sx={{ padding: theme.spacing(1) }}>
        <Box sx={{ mb: theme.spacing(1) }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2">Name (optional)</Typography>
            <RobotoTooltip title="120 character limit">
              <HelpOutlineIcon
                sx={{ fontSize: "1rem", ml: 1, cursor: "pointer" }}
                color="info"
              />
            </RobotoTooltip>
          </Box>
          <Typography variant="caption">
            Add a name for this dataset.
          </Typography>
        </Box>

        <TextField
          id="outlined-action-desc"
          placeholder="Enter a name"
          size="small"
          multiline
          fullWidth
          value={name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
          }}
          inputProps={{
            style: { fontSize: "0.9rem" },
            maxLength: 120,
          }}
          sx={{
            width: "85%",
          }}
        />

        <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />

        <Box sx={{ mb: theme.spacing(1) }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2">Tags (optional)</Typography>
          </Box>
          <Typography variant="caption">
            Add tags to your dataset so it&apos;s easier to find later.
          </Typography>
        </Box>

        <TagsGroup>
          <Tags
            tags={tags}
            onDeleteTag={(tag) => {
              setTags((prev) => prev.filter((t) => t !== tag));
            }}
            truncateText={false}
          />
          <TagInput
            autocompleteType={AutocompleteType.DatasetTags}
            onAddTag={(tag) => {
              setTags((prev) => [...prev, tag]);
            }}
            tags={tags}
          />
        </TagsGroup>

        <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />

        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2">Metadata (optional)</Typography>
          </Box>
          <Typography variant="caption">
            Add key-value pairs to capture any key details, e.g. softwareVersion
            = 1.0.3.
          </Typography>
        </Box>

        <MetadataView metadata={metadata} onChange={setMetadata} />
        <MetadataForm
          metadata={metadata}
          autocompleteType={AutocompleteType.DatasetMetadataKeys}
          onAddMetadata={(key, value) => {
            setMetadata({
              ...metadata,
              [key]: value,
            });
          }}
        />

        {currentOrganization?.org_id && (
          <CustomFieldValuesSection
            entityType={TargetEntityType.Dataset}
            orgId={currentOrganization.org_id}
            values={customFieldValues}
            onChange={setCustomFieldValues}
            header={
              <>
                <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">
                    Custom Fields (optional)
                  </Typography>
                  <Typography variant="caption">
                    Set values for this dataset&apos;s custom fields.
                  </Typography>
                </Box>
              </>
            }
          />
        )}

        <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />

        {createDatasetMutation.isError && (
          <Alert
            severity="error"
            sx={{ mb: theme.spacing(1.5) }}
            onClose={() => {
              createDatasetMutation.reset();
            }}
          >
            {formatSubmitError(createDatasetMutation.error)}
          </Alert>
        )}

        <Box
          sx={{
            mt: theme.spacing(1),
            display: "flex",
            alignItems: "center",
          }}
        >
          <RobotoButton
            variant="primary"
            onClick={() => {
              trackEvent("CreateDatasetFormSubmitted", {
                source: "CreateDatasetPopover",
              });
              createDataset();
            }}
            disabled={createDatasetMutation.isPending}
            loading={createDatasetMutation.isPending}
          >
            Create dataset
          </RobotoButton>
        </Box>
      </Box>
    </Box>
  );
}

function formatSubmitError(err: unknown): string {
  if (err instanceof RobotoDomainException && err.isClientError()) {
    return err.message;
  }
  return "Failed to create dataset. Try again or reach out to support@roboto.ai if problem persists.";
}
